import type { Database } from "@/lib/database.types";
import {
  buildNearMePreferencesFromAdminDefaults,
  LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY,
  type NearMeUserDefaultsPolicy,
} from "@/lib/notifications/near-me-user-defaults-policy";
import { defaultSellerAvailabilityWhatsappEnabled } from "@/lib/notifications/near-me-whatsapp-preference-sync";

/** Client-side fallback when admin policy is unavailable (matches launch defaults). */
export const GROWTH_PHASE_NEAR_ME_GRANULARITY =
  LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY.near_me_default_granularity;
export const GROWTH_PHASE_NEAR_ME_DELIVERY_MODE =
  LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY.near_me_default_delivery_mode_state;

export type GrowthPhaseNotificationPreferencesInsert =
  Database["public"]["Tables"]["notification_preferences"]["Insert"];

function readGrowthPhaseFlag(
  serverFlag: string | undefined,
  publicFlag: string | undefined,
): boolean {
  const value = serverFlag ?? publicFlag;
  return value !== "false";
}

/** Client-side gate for growth-phase default row shape. */
export function isGrowthPhaseNotificationDefaultsEnabled(): boolean {
  return readGrowthPhaseFlag(
    undefined,
    process.env.NEXT_PUBLIC_GROWTH_PHASE_NOTIFICATION_DEFAULTS_ENABLED,
  );
}

/** Server-side gate (API routes, create-profile). */
export function isGrowthPhaseNotificationDefaultsEnabledServer(): boolean {
  return readGrowthPhaseFlag(
    process.env.GROWTH_PHASE_NOTIFICATION_DEFAULTS_ENABLED,
    process.env.NEXT_PUBLIC_GROWTH_PHASE_NOTIFICATION_DEFAULTS_ENABLED,
  );
}

/**
 * Growth-phase default notification_preferences row.
 * Near-me granularity + delivery come from admin policy when provided; otherwise launch defaults.
 */
export function buildGrowthPhaseNotificationPreferencesRow(
  userId: string,
  options?: {
    nowIso?: string;
    nearMeEnabled?: boolean;
    nearMePolicy?: NearMeUserDefaultsPolicy;
    /** Profile phone — when valid, near-me WhatsApp defaults on (Layer 1). */
    phoneDigits?: string | null;
  },
): GrowthPhaseNotificationPreferencesInsert {
  const nowIso = options?.nowIso ?? new Date().toISOString();
  const nearMeEnabled = options?.nearMeEnabled ?? true;
  const nearMePolicy =
    options?.nearMePolicy ?? LAUNCH_NEAR_ME_USER_DEFAULTS_POLICY;
  const nearMeFields = buildNearMePreferencesFromAdminDefaults(nearMePolicy, {
    nearMeEnabled,
    phoneDigits: options?.phoneDigits,
    nowIso,
  });

  return {
    user_id: userId,
    notify_orders: true,
    notify_payments: true,
    notify_listings: true,
    notify_listings_email: true,
    notify_disputes: false,
    notify_marketing: false,
    notify_platform_updates: true,
    notify_orders_email: false,
    notify_payments_email: false,
    ...nearMeFields,
    notify_seller_availability_whatsapp:
      defaultSellerAvailabilityWhatsappEnabled(options?.phoneDigits),
    updated_at: nowIso,
  };
}
